import postcss from "postcss";
import selectorParser from "postcss-selector-parser";

const SURVEY_ROOT = '[data-survey-root="true"]';
const MAX_CSS_BYTES = 20 * 1024;
const ALLOWED_AT_RULES = new Set(["media", "supports", "container"]);

const decodeCssEscapes = (value: string) =>
  value.replace(/\\([0-9a-f]{1,6}\s?|.)/gi, (_, escape: string) => {
    const hex = escape.trim();
    return /^[0-9a-f]{1,6}$/i.test(hex)
      ? String.fromCodePoint(parseInt(hex, 16))
      : escape;
  });

export const scopeCustomCss = (css: unknown): string => {
  if (
    import.meta.env.VITE_DISABLE_CUSTOM_CSS === "true" ||
    typeof css !== "string" ||
    !css.trim() ||
    new TextEncoder().encode(css).length > MAX_CSS_BYTES ||
    /[<>]/.test(css)
  )
    return "";

  try {
    const root = postcss.parse(css);
    let unsafe = false;
    root.walkAtRules((rule) => {
      if (!ALLOWED_AT_RULES.has(decodeCssEscapes(rule.name).toLowerCase()))
        unsafe = true;
    });
    root.walkDecls((declaration) => {
      if (declaration.parent?.type !== "rule") unsafe = true;
      const value = decodeCssEscapes(declaration.value).replace(
        /\/\*[\s\S]*?\*\//g,
        "",
      );
      if (/javascript\s*:/i.test(value)) unsafe = true;
    });
    if (unsafe) return "";

    root.walkRules((rule) => {
      const scoped: string[] = [];
      selectorParser((selectors) => {
        selectors.each((selector) => {
          selector.walkTags((tag) => {
            if (
              ["html", "body"].includes(
                decodeCssEscapes(tag.value).toLowerCase(),
              )
            )
              unsafe = true;
          });
          selector.walkPseudos((pseudo) => {
            if (
              [":root", ":host", ":host-context"].includes(
                pseudo.value.toLowerCase(),
              )
            )
              unsafe = true;
          });
          selector.walkNesting(() => {
            unsafe = true;
          });
          const text = selector.toString().trim();
          scoped.push(
            selector.nodes[0]?.type === "pseudo" &&
              selector.nodes[0].value.toLowerCase() === ":scope"
              ? `${SURVEY_ROOT}${text.slice(6)}`
              : `${SURVEY_ROOT} ${text}`,
          );
        });
      }).processSync(rule.selector);
      rule.selector = scoped.join(", ");
    });
    if (unsafe || root.nodes.some((node) => node.type === "decl")) return "";
    return root.toString();
  } catch {
    return "";
  }
};
